import {
    CreatorProfileTopic,
    GetCreatorProfileResponse,
    GetTopicResponse,
    TopicSearchResult
} from '../../types';
import { LitElement, css, html } from 'lit';
import { addTopicsToCreatorProfile, editPinForCreatorProfileTopic, getCreatorProfile, getTopicsFromCreatorProfile, removeTopicFromCreatorProfile } from '../../helpers/api/creator-profile-api';
import { customElement, query, state } from 'lit/decorators';

import { Dialog } from '@material/mwc-dialog';
import { getQueryStringParameters, swapUrlForCustomDomain } from '../../helpers/urls';
import { getTopicsforBackpack } from '../../helpers/api/topics-api';
import { isDefined } from '../../helpers/utils';
import { getMainPage } from '../../helpers/page-name';

type EditArea = 'Maps' | 'Profile Information';

@customElement('edit-creator-profile-page')
export class EditCreatorProfilePage extends LitElement {
    static amplitude_callsite: string = 'Edit Creator Profile Page';

    @query('#addMapsModalComponent')
    addMapsModalComponent?: Dialog;

    @state()
    topics: TopicSearchResult[] = [];

    @state()
    creatorProfile?: GetCreatorProfileResponse;

    @state()
    creatorTopics: CreatorProfileTopic[] = [];

    @state()
    editArea?: EditArea = 'Profile Information';

    @state()
    profileId?: string;

    connectedCallback() {
        super.connectedCallback();    }

    // listen for changes to the folderId
    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('profileId')) {
            await this.startup();
        }
    }

    async firstUpdated() {
        await this.startup();
    }

    async setUpWithUserProfile(profileId: string) {
        this.profileId = profileId;
        await this.startup();
    }

    private async startup() {
        const queryString = getQueryStringParameters();
        this.profileId = this.profileId ?? queryString.get('profile_id');

        this.topics = await getTopicsforBackpack(EditCreatorProfilePage.amplitude_callsite);

        if (isDefined(this.profileId)) {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                const creatorProfile = await getCreatorProfile(EditCreatorProfilePage.amplitude_callsite, this.profileId ?? '');

                // If someone doesnt own to this profile, 404
                if (!creatorProfile.is_owner) {
                    console.error('User does not own this profile, redirecting to 404');
                    return;
                }

                await this.setProfileInfo(creatorProfile);

            }, 'An error has occurred');
        } else {
            console.error('No profile id provided4');
        }

        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    private async setProfileInfo(creatorProfile: GetCreatorProfileResponse) {
        this.creatorProfile = creatorProfile;
        // the topics on creator profile only have ids - we fetch full topics here, and then need to create new models that merge the two
        const topics = await getTopicsFromCreatorProfile(EditCreatorProfilePage.amplitude_callsite, this.creatorProfile._id.$oid);

        this.creatorTopics = this.creatorProfile?.topics.map(profileTopic => {
            const populatedTopic = topics.find(topic => topic._id.$oid === profileTopic.topic_id.$oid);
            if (isDefined(populatedTopic)) {
                return {
                    topic: populatedTopic,
                    is_pinned: profileTopic.is_pinned
                };
            }

            throw new Error('Topic data from creator profile doesnt match topic data from topics api');
        });
    }

    private triggerAddTopics() {
        if (isDefined(this.addMapsModalComponent)) {
            this.addMapsModalComponent.open = true;
        }
    }

    private activateEditArea(area: EditArea) {
        this.editArea = area;
    }

    private async onAddTopicsToProfile(e: CustomEvent) {
        if (isDefined(this.creatorProfile)) {
            const topicsToAdd = e.detail.selectedTopics.filter((topic: GetTopicResponse) => !this.creatorTopics.find(creatorTopic => creatorTopic.topic._id.$oid === topic._id.$oid));
            await addTopicsToCreatorProfile(EditCreatorProfilePage.amplitude_callsite, this.creatorProfile?._id.$oid, topicsToAdd.map((topic: GetTopicResponse) => topic._id.$oid));

            this.creatorTopics = e.detail.selectedTopics.map((topic: GetTopicResponse) => {
                const populatedTopic = this.creatorTopics.find(creatorTopic => topic._id.$oid === creatorTopic.topic._id.$oid);
                if (isDefined(populatedTopic)) {
                    return {
                        topic,
                        is_pinned: populatedTopic.is_pinned
                    };
                }

                return {
                    topic,
                    is_pinned: false
                };
            });
        }
    }

    private async removeTopicFromProfile(topicToRemove: CreatorProfileTopic) {
        if (isDefined(this.creatorProfile)) {
            await removeTopicFromCreatorProfile(EditCreatorProfilePage.amplitude_callsite, this.creatorProfile?._id.$oid, topicToRemove.topic._id.$oid);

            this.creatorTopics = this.creatorTopics.filter(creatorTopic => topicToRemove.topic._id.$oid !== creatorTopic.topic._id.$oid);
        }
    }

    private async pinTopic(topicToPin: CreatorProfileTopic) {
        if (isDefined(this.creatorProfile)) {
            const addPin = !topicToPin.is_pinned;
            await editPinForCreatorProfileTopic(EditCreatorProfilePage.amplitude_callsite, this.creatorProfile?._id.$oid, topicToPin.topic._id.$oid, addPin);

            this.creatorTopics = this.creatorTopics.map(creatorTopic => {
                if (creatorTopic.topic._id.$oid === topicToPin.topic._id.$oid) {
                    creatorTopic.is_pinned = addPin;
                }
                return creatorTopic;
            });
        }
    }

    private onUpdateProfile(e: CustomEvent) {
        this.creatorProfile = e.detail.updatedProfile;
    }

    private async onSelectCreatorProfile(e: any) {
        const creatorProfile = await getCreatorProfile(EditCreatorProfilePage.amplitude_callsite, e.detail.creatorProfileId);

        await this.setProfileInfo(creatorProfile);
    }

    private onUpdateFilterType(e: any) {
        window.location.assign(e.detail.filterTypeInfo.url);
    }

    private changeTab(e: CustomEvent | any) {
        if (e.detail.tabIndex === 0) {
            this.editArea = 'Profile Information';
        } else if (e.detail.tabIndex === 1) {
            this.editArea = 'Maps';
        }
    }

    protected render() {
        return html`
            <div class="edit-page__page-container">
                <div class="edit-page__main-content">
                    <span class="edit-page__header">
                        <h1>Manage @${this.creatorProfile?.username}</h1>
                        <copy-link-component
                            .showLabel=${false}
                            .text="${swapUrlForCustomDomain(this.creatorProfile?.link_set.public_link ?? '')}"
                            successMessage="Link copied successfully."
                            textType="Share"
                            pageName="Admin Portal">
                            Copy Link
                        </copy-link-component>
                    </span>

                    <div class="edit-page__tabs">

                        <tabs-component
                            id="tabsComponent"
                            .options=${['Profile Information', 'Maps']}
                            @on-select-tab=${this.changeTab}
                        ></tabs-component>
                    </div>

                    <div class="edit-page__edit-area">
                        ${this.editArea === 'Profile Information' ?
                            html`
                                <creator-profile-form
                                    .creatorProfile=${this.creatorProfile}
                                    @on-update-profile=${this.onUpdateProfile}>
                                </creator-profile-form>
                            `
                        :   html`
                                <form-button @click="${this.triggerAddTopics}" class="edit-page__action-button edit-page__action-button--yellow">
                                    <mwc-icon>add</mwc-icon>
                                    Add Maps
                                </form-button>
                                <div class="edit-page__topics">
                                    ${this.creatorTopics?.map(creatorTopic => {
                                        return html`<div class="column-item"><backpack-card .creatorProfile=${this.creatorProfile}
                                                                   .topic=${creatorTopic.topic}
                                                                   .canPin=${true}
                                                                   ?isOnly=${this.creatorTopics.length === 1}
                                                                   ?isPinned=${creatorTopic.is_pinned}
                                                                   @on-remove-from-creator-profile=${async () => await this.removeTopicFromProfile(creatorTopic)}
                                                                   @on-pin-topic=${async () => await this.pinTopic(creatorTopic)}>
                                                    </backpack-card></div>`;
                                    })}
                                </div>
                            `
                        }
                    </div>
                </div>


            </div>
            <div class="edit-page__bottom-tabs mobileOnly">
                <a class="tabItem" href="#folder">
                    <mwc-icon>folder</mwc-icon>
                    <div>Folders</div>
                </a>
                <a class="tabItem" href="#home">
                    <mwc-icon>home</mwc-icon>
                    <div>Backpack</div>
                </a>
                <div class="tabItem active">
                    <mwc-icon>account_circle</mwc-icon>
                    <div>Profiles</div>
                </div>
            </div>

            <mwc-dialog id="addMapsModalComponent" class="dialog" hideActions>
                <modal-dialog size="lg" @close="${() => this.addMapsModalComponent?.close()}">
                    <add-maps-modal
                        slot="body"
                        .selectedTopics=${this.creatorTopics.map(creatorTopics => creatorTopics.topic)}
                        @add-topics-to-profile="${this.onAddTopicsToProfile}"
                        @close="${() => this.addMapsModalComponent?.close()}">
                    </add-maps-modal>
                </modal-dialog>
            </mwc-dialog>


            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .edit-page__topics {
                padding-bottom: 3.5rem;
                width: 100%;
                box-sizing: border-box;
                margin: 0px -10px;
                display: flex;
                flex-wrap: wrap;
                overflow-y: auto; // DO NOT REMOVE - this allows scrolling on mobile
            }

            .column-item {
                width: 25%;
                min-width: 150px;
                padding: 10px;
                box-sizing: border-box;
            }

            .edit-page__subheader {
                margin-bottom: 20px;
            }

            .edit-page__header {
                margin: 6px 0;
            }

            .edit-page__tabs {
                display: flex;
            }

            .edit-page__edit-area {
                padding: 14px;
            }

            .edit-page__main-content {
                flex: 1;
                width: 100%;
                padding: 1.5rem;
                box-sizing: border-box;
                overflow: auto;
            }

            .edit-page__sidebar {
                width: 260px;
                border-right: 1px solid #ECECEC;
                box-sizing: border-box;
            }

            .edit-page__page-container {
                display: flex;
                width: 100%;
                box-sizing: border-box;
                align-items: stretch;
            }

            .edit-page__bottom-tabs {
                position: fixed;
                bottom: 0;
                width: 100%;
                box-sizing: border-box;
                align-items: center;
                justify-content: space-around;
                background: var(--main-secondary-color);
                color: var(--theme-white-color);
            }

            .edit-page__bottom-tabs .tabItem {
                display: flex;
                flex: 1;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                padding: 0.5rem;
                color: var(--theme-white-color);
                border-top: 4px solid transparent;
                cursor: pointer;
                text-decoration: none;
            }

            .edit-page__bottom-tabs .tabItem.active {
                color: var(--main-primary-color);
                border-top: 4px solid var(--main-primary-color);
            }

            @media (min-width: 551px) {
                .desktopOnly {
                    display: block;
                }

                .mobileOnly {
                    display: none;
                }

                .edit-page__header {
                    display: flex;
                    justify-content: space-between;
                    align-items: baseline;
                }
            }

            @media (max-width: 550px) {
                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: block;
                }

                .edit-page__header {
                    margin-bottom: 20px;
                    display: block;
                }

                .edit-page__bottom-tabs.mobileOnly {
                    display: flex;
                }

                .edit-page__page-container {
                    flex-direction: column;
                }

                .edit-page__sidebar {
                    width: 100%;
                    height: auto;
                }

                .edit-page__main-content {
                    padding: 1rem;
                }

                .column-item {
                    width: 50%;
                    min-width: 150px;
                }
            }

        `;
    }
}
